// src/pages/admin/adminpanel/OverviewPanel.jsx
import React from 'react';

// ── StatCard widget (only used in Overview) ──────────────
function StatCard({ label, value, icon, color, bg }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10
                    hover:border-primary/20 transition-all flex items-start justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-[11px] font-bold uppercase tracking-widest text-secondary">
          {label}
        </p>
        <p className="text-3xl font-black text-on-surface mt-1">{value ?? '—'}</p>
        <p className={`text-xs font-bold mt-1 flex items-center gap-1 ${color}`}>
          <span className="material-symbols-outlined text-sm">trending_up</span>
          Live data
        </p>
      </div>
      <span className={`material-symbols-outlined ${color} ${bg}
                        p-2.5 rounded-xl text-[22px] flex-shrink-0`}>
        {icon}
      </span>
    </div>
  );
}

// ── PlatformHealth widget ─────────────────────────────────
function PlatformHealth({ stats = {} }) {
  const metrics = [
    { label: 'Total Orders',    value: stats.total_orders   ?? 0, pct: Math.min((stats.total_orders   ?? 0) * 5, 100),  color: 'bg-blue-400'    },
    { label: 'Total Animals',   value: stats.total_animals  ?? 0, pct: Math.min((stats.total_animals  ?? 0) * 10, 100), color: 'bg-emerald-400' },
    { label: 'Registered Users',value: stats.total_users    ?? 0, pct: Math.min((stats.total_users    ?? 0) * 15, 100), color: 'bg-purple-400'  },
    { label: 'Meat Products',   value: stats.total_products ?? 0, pct: Math.min((stats.total_products ?? 0) * 10, 100), color: 'bg-amber-400'   },
  ];

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm">Platform Health</h3>
        <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600
                         bg-emerald-50 px-2 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </span>
      </div>
      <div className="space-y-3">
        {metrics.map((m) => (
          <div key={m.label}>
            <div className="flex justify-between text-xs font-semibold mb-1">
              <span className="text-secondary">{m.label}</span>
              <span className="text-on-surface font-bold">{m.value}</span>
            </div>
            <div className="h-1.5 bg-surface-container rounded-full overflow-hidden">
              <div className={`h-full ${m.color} rounded-full transition-all duration-700`}
                   style={{ width: `${m.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── RecentActivity widget ─────────────────────────────────
function RecentActivity({ data = [], onViewAll }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm">Recent Activity</h3>
        <button onClick={onViewAll} className="text-[11px] font-bold text-primary hover:underline">
          View All →
        </button>
      </div>
      {data.length === 0
        ? <p className="text-xs text-secondary text-center py-4">No activity yet</p>
        : <div className="space-y-3">
            {data.map((a, i) => {
              const cfg = a.type === 'order'
                ? { icon: 'shopping_cart', color: 'text-purple-500', bg: 'bg-purple-50', text: 'New order placed',     sub: `${a.subject} by ${a.actor}` }
                : a.type === 'inquiry'
                ? { icon: 'forum',         color: 'text-amber-500',  bg: 'bg-amber-50',  text: 'New inquiry received', sub: `${a.subject} — ${a.actor}`  }
                : { icon: 'person_add',    color: 'text-blue-500',   bg: 'bg-blue-50',   text: 'New user registered',  sub: `${a.actor} — ${a.subject}`  };
              return (
                <div key={i} className="flex items-start gap-3">
                  <span className={`material-symbols-outlined ${cfg.color} ${cfg.bg}
                                    p-1.5 rounded-lg text-[16px] flex-shrink-0 mt-0.5`}>
                    {cfg.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-on-surface">{cfg.text}</p>
                    <p className="text-[11px] text-secondary truncate">{cfg.sub}</p>
                  </div>
                  <span className="text-[10px] text-outline flex-shrink-0 mt-0.5">
                    {new Date(a.created_at).toLocaleDateString('en-IN')}
                  </span>
                </div>
              );
            })}
          </div>
      }
    </div>
  );
}

// ── BreedPerformance widget ───────────────────────────────
function BreedPerformance({ data = [] }) {
  const colors = ['bg-primary','bg-emerald-400','bg-blue-400','bg-amber-400','bg-purple-400'];
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10">
      <h3 className="font-bold text-sm mb-4">Category Performance</h3>
      {data.length === 0
        ? <p className="text-xs text-secondary text-center py-4">No data yet</p>
        : <div className="space-y-4">
            {data.map((b, i) => (
              <div key={b.name}>
                <div className="flex justify-between text-xs font-bold mb-1">
                  <span>{b.name}</span>
                  <span className="text-primary">{b.pct}%</span>
                </div>
                <div className="h-1.5 bg-surface-container rounded-full overflow-hidden">
                  <div className={`h-full ${colors[i % colors.length]} rounded-full`}
                       style={{ width: `${b.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
      }
    </div>
  );
}

// ── UsersByRole widget ────────────────────────────────────
function UsersByRole({ userStats }) {
  const roles = [
    { role: 'Customers', val: userStats.customers, color: 'text-blue-600',    bg: 'bg-blue-50'    },
    { role: 'Farmers',   val: userStats.farmers,   color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { role: 'Butchers',  val: userStats.butchers,  color: 'text-amber-600',   bg: 'bg-amber-50'   },
    { role: 'Admins',    val: userStats.admins,    color: 'text-red-600',     bg: 'bg-red-50'     },
  ];
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10">
      <h3 className="font-bold text-sm mb-4">Users by Role</h3>
      <div className="grid grid-cols-2 gap-3">
        {roles.map((r) => (
          <div key={r.role} className={`p-3 rounded-xl ${r.bg}`}>
            <p className={`text-xl font-black ${r.color}`}>{r.val ?? 0}</p>
            <p className="text-[11px] font-semibold text-secondary">{r.role}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SystemStatus widget ───────────────────────────────────
function SystemStatus() {
  return (
    <div className="bg-emerald-950 rounded-2xl p-5 text-white">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-xs font-bold">All Systems Operational</span>
      </div>
      <div className="space-y-1.5 text-[11px] font-mono text-emerald-300/70">
        <div className="flex justify-between">
          <span>Backend</span>
          <span className="text-emerald-300">:4000 ✓</span>
        </div>
        <div className="flex justify-between">
          <span>Database</span>
          <span className="text-emerald-300">MySQL ✓</span>
        </div>
        <div className="flex justify-between">
          <span>Frontend</span>
          <span className="text-emerald-300">:3000 ✓</span>
        </div>
      </div>
    </div>
  );
}

// ── ToastNotification ─────────────────────────────────────
function ToastNotification() {
  const [visible, setVisible] = React.useState(true);
  if (!visible) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3
                    bg-emerald-900 text-white px-5 py-3.5 rounded-2xl shadow-2xl
                    border border-emerald-700 animate-fade-in max-w-xs">
      <span className="material-symbols-outlined text-emerald-300 text-[20px]">
        trending_up
      </span>
      <div className="flex-1">
        <p className="text-xs font-bold">Platform revenue up 12% today</p>
        <p className="text-[10px] text-emerald-300 mt-0.5">Compared to yesterday</p>
      </div>
      <button onClick={() => setVisible(false)}
        className="text-emerald-400 hover:text-white transition-colors ml-1">
        <span className="material-symbols-outlined text-[16px]">close</span>
      </button>
    </div>
  );
}

// ── PromoBanner ───────────────────────────────────────────
function PromoBanner() {
  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-r
                    from-emerald-900 via-emerald-800 to-emerald-700 p-8
                    flex items-center justify-between shadow-sm border border-emerald-700/30">
      {/* Left content */}
      <div className="z-10">
        <span className="text-[10px] font-black uppercase tracking-widest
                         text-emerald-300 bg-emerald-900/50 px-2 py-1 rounded-full">
          Upcoming Event
        </span>
        <h2 className="text-2xl font-black text-white mt-2">
          🐐 Livestock Festival 2025
        </h2>
        <p className="text-emerald-200 text-sm mt-1 max-w-sm">
          Premium goat & sheep auction — Bengaluru · June 14–16
        </p>
        <button className="mt-4 px-5 py-2 bg-white text-emerald-900 rounded-full
                           text-xs font-black hover:bg-emerald-100 transition-all">
          Promote on Platform →
        </button>
      </div>

      {/* Decorative circles */}
      <div className="absolute right-0 top-0 w-48 h-48 bg-emerald-600/20
                      rounded-full -translate-y-1/2 translate-x-1/4" />
      <div className="absolute right-12 bottom-0 w-32 h-32 bg-emerald-500/10
                      rounded-full translate-y-1/3" />

      {/* Right emoji */}
      <div className="text-7xl z-10 hidden lg:block select-none">🐑</div>
    </div>
  );
}

// ── Main OverviewPanel export ─────────────────────────────
export default function OverviewPanel({
  userStats, animals, butcherProducts = [], pendingCount,
  onModAction, onProductModAction, onViewAll,
  breedStats = [], recentActivity = [],
  platformStats = {},
}) {
  const pendingAnimals   = animals.filter(a => a.status === 'pending');
  const pendingProducts  = butcherProducts.filter(p => p.status === 'pending');

  return (
    <div className="space-y-6">
      <ToastNotification />

      {/* Stat cards — now 5 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
        <StatCard label="Total Users"     value={userStats.total}          icon="group"           color="text-blue-600"    bg="bg-blue-50"    />
        <StatCard label="Farmers"         value={userStats.farmers}        icon="storefront"      color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard label="Total Animals"   value={animals.length}           icon="pets"            color="text-primary"     bg="bg-primary/10" />
        <StatCard label="Meat Products"   value={butcherProducts.length}   icon="storefront"      color="text-amber-600"   bg="bg-amber-50"   />
        <StatCard label="Pending Review"  value={pendingCount}             icon="pending_actions" color="text-red-600"     bg="bg-red-50"     />
      </div>

      {/* Middle row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10">
          <div className="flex justify-between items-center mb-5">
            <h3 className="font-bold text-lg">Pending Listings</h3>
            <button onClick={onViewAll} className="text-xs font-bold text-primary hover:underline">
              View All →
            </button>
          </div>

          {/* Animals pending */}
          {pendingAnimals.length > 0 && (
            <>
              <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-2">
                🐐 Animals
              </p>
              <div className="space-y-3 mb-5">
                {pendingAnimals.slice(0, 3).map(item => (
                  <div key={item.id} className="flex items-center gap-3">
                    {item.image_url
                      ? <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      : <div className="w-10 h-10 rounded-lg bg-surface-container flex items-center justify-center flex-shrink-0">
                          <span className="material-symbols-outlined text-outline text-lg">pets</span>
                        </div>
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{item.name}</p>
                      <p className="text-[10px] text-secondary">{item.category} · {item.seller_name}</p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => onModAction(item.id, 'active')}
                        className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100
                                  text-emerald-700 hover:bg-emerald-600 hover:text-white transition-all">
                        Approve
                      </button>
                      <button onClick={() => onModAction(item.id, 'draft')}
                        className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-100
                                  text-red-600 hover:bg-red-600 hover:text-white transition-all">
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Butcher products pending */}
          {pendingProducts.length > 0 && (
            <>
              <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-2">
                🥩 Meat Products
              </p>
              <div className="space-y-3">
              {pendingProducts.slice(0, 3).map(item => (
                <div key={item.id} className="flex items-center gap-3">
                  {item.image_url
                    ? <img src={item.image_url} alt={item.name}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    : <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-amber-400 text-lg">lunch_dining</span>
                      </div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{item.name}</p>
                    <p className="text-[10px] text-secondary">{item.category} · ₹{Number(item.price_inr).toLocaleString('en-IN')} · {item.butcher_name}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => onProductModAction(item.id, 'active')}
                      className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100
                                text-emerald-700 hover:bg-emerald-600 hover:text-white transition-all">
                      Approve
                    </button>
                    <button onClick={() => onProductModAction(item.id, 'inactive')}
                      className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-100
                                text-red-600 hover:bg-red-600 hover:text-white transition-all">
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          {pendingAnimals.length === 0 && pendingProducts.length === 0 && (
            <div className="py-10 text-center text-secondary">
              <span className="material-symbols-outlined text-3xl text-outline-variant">check_circle</span>
              <p className="mt-2 text-sm font-medium">No pending listings</p>
            </div>
          )}
        </div>

        {/* Right column — unchanged */}
        <div className="space-y-5">
          <PlatformHealth stats={platformStats} />
          <BreedPerformance data={breedStats} />
          <UsersByRole userStats={userStats} />
          <SystemStatus />
        </div>
      </div>

      {/* Bottom row — unchanged */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
  <div className="lg:col-span-1">
    <RecentActivity data={recentActivity} onViewAll={onViewAll} />
  </div>
  <div className="lg:col-span-2"><PromoBanner /></div>
</div>
    </div>
  );
}